import {
  AWSTranscribeAlternative,
  AWSTranscribeAudioSegment,
  AWSTranscribeItem,
  AWSTranscribeResponse,
  AWSTranscribeResults,
  AWSTranscribeSpeakerLabelItem,
  AWSTranscribeSpeakerLabelSegment,
  AWSTranscribeSpeakerLabels,
  AWSTranscribeTranscript,
} from "@tellescope/types-models";
import { discriminatedStringUnionIncludes } from "./discriminated-string-union-includes";

const isRecord = (data: unknown): data is Record<string, unknown> => typeof data === "object" && data !== null;

const isString = (value: unknown): value is string => typeof value === "string";
const isNumber = (value: unknown): value is number => typeof value === "number";

const isValidAlternative = (data: unknown): data is AWSTranscribeAlternative => {
  if (!isRecord(data)) return false;
  if (!isString(data.content)) return false;
  if ("confidence" in data && data.confidence !== undefined && !isString(data.confidence)) return false;
  return true;
};

const isValidItem = (data: unknown): data is AWSTranscribeItem => {
  if (!isRecord(data)) return false;
  if (!isNumber(data.id)) return false;
  if (data.type !== "pronunciation" && data.type !== "punctuation") return false;
  if (!Array.isArray(data.alternatives) || !data.alternatives.every(isValidAlternative)) return false;
  if ("start_time" in data && data.start_time !== undefined && !isString(data.start_time)) return false;
  if ("end_time" in data && data.end_time !== undefined && !isString(data.end_time)) return false;
  if ("speaker_label" in data && data.speaker_label !== undefined && !isString(data.speaker_label)) return false;
  return true;
};

const isValidSpeakerLabelItem = (data: unknown): data is AWSTranscribeSpeakerLabelItem => {
  if (!isRecord(data)) return false;
  if (!isString(data.speaker_label)) return false;
  if (!isString(data.start_time)) return false;
  if ("end_time" in data && data.end_time !== undefined && !isString(data.end_time)) return false;
  return true;
};

const isValidSpeakerLabelSegment = (data: unknown): data is AWSTranscribeSpeakerLabelSegment => {
  if (!isRecord(data)) return false;
  if (!isString(data.start_time)) return false;
  if (!isString(data.end_time)) return false;
  if (!isString(data.speaker_label)) return false;
  if (!Array.isArray(data.items) || !data.items.every(isValidSpeakerLabelItem)) return false;
  return true;
};

const isValidSpeakerLabels = (data: unknown): data is AWSTranscribeSpeakerLabels => {
  if (!isRecord(data)) return false;
  if (!Array.isArray(data.segments) || !data.segments.every(isValidSpeakerLabelSegment)) return false;
  if (!isString(data.channel_label)) return false;
  if (!isNumber(data.speakers)) return false;
  return true;
};

const isValidAudioSegment = (data: unknown): data is AWSTranscribeAudioSegment => {
  if (!isRecord(data)) return false;
  if (!isNumber(data.id)) return false;
  if (!isString(data.transcript)) return false;
  if (!isString(data.start_time)) return false;
  if (!isString(data.end_time)) return false;
  if (!isString(data.speaker_label)) return false;
  if (!Array.isArray(data.items) || !data.items.every(isNumber)) return false;
  return true;
};

const isValidTranscript = (data: unknown): data is AWSTranscribeTranscript => {
  if (!isRecord(data)) return false;
  if (!isString(data.transcript)) return false;
  return true;
};

const VALID_STATUSES: AWSTranscribeResponse["status"][] = ["COMPLETED", "IN_PROGRESS", "FAILED"];

const isValidResults = (data: unknown): data is AWSTranscribeResults => {
  if (!isRecord(data)) return false;
  if (!Array.isArray(data.transcripts) || !data.transcripts.every(isValidTranscript)) return false;
  if (!Array.isArray(data.items) || !data.items.every(isValidItem)) return false;
  if ("speaker_labels" in data && data.speaker_labels !== undefined && !isValidSpeakerLabels(data.speaker_labels))
    return false;
  if (
    "audio_segments" in data &&
    data.audio_segments !== undefined &&
    (!Array.isArray(data.audio_segments) || !data.audio_segments.every(isValidAudioSegment))
  )
    return false;
  return true;
};

const isValidResponse = (data: unknown): data is AWSTranscribeResponse => {
  if (!isRecord(data)) return false;
  if (!isString(data.jobName)) return false;
  if (!isString(data.accountId)) return false;
  if (!isString(data.status) || !discriminatedStringUnionIncludes(VALID_STATUSES, data.status)) return false;
  if (!isValidResults(data.results)) return false;
  return true;
};

export const parseCallRecordingTranscriptionData = (data: string | undefined): AWSTranscribeResponse | undefined => {
  if (!data) return undefined;

  try {
    const parsedData: unknown = JSON.parse(data);
    if (!isValidResponse(parsedData)) return undefined;
    return parsedData;
  } catch {
    return undefined;
  }
};
